/**
 * Custom Hook para integración de PinPad Web v2.6.0
 * 
 * Encapsula toda la lógica de comunicación con el iframe:
 * - Inicialización del iframe
 * - Gestión del MessageChannel
 * - Estados de operaciones
 * - Manejo de errores
 * 
 * Uso:
 * ```jsx
 * const pinpad = usePinpadWeb('QA')
 * 
 * // Inicializar
 * await pinpad.initialize()
 * 
 * // Login
 * const loginResult = await pinpad.login({
 *   User: 'usuario',
 *   Pwd: 'password',
 *   Environment: 'QA'
 * })
 * 
 * // Obtener llaves
 * const keysResult = await pinpad.getKeysRSA({
 *   User: 'usuario',
 *   Pwd: 'password',
 *   IdCompany: '123',
 *   IdBranch: '456',
 *   Country: 'MX',
 *   Environment: 'QA'
 * })
 * ```
 * 
 * @module hooks/usePinpadWeb
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import iframeService from '../services/pinpad-v2/iframe.service'
import messageChannelService from '../services/pinpad-v2/message-channel.service'
import authService from '../services/pinpad-v2/operations/auth.service'
import sessionService from '../services/pinpad-v2/operations/session.service'
import cardService from '../services/pinpad-v2/operations/card.service'
import merchantService from '../services/pinpad-v2/operations/merchant.service'
import transactionService from '../services/pinpad-v2/operations/transaction.service'
import voucherService from '../services/pinpad-v2/operations/voucher.service'
import { logInfo, logError, logWarn } from '../utilities/pinpad-v2/logger'
import { logFlowStep } from '../utilities/pinpad-v2/flow-logger'

/**
 * Hook principal para PinPad Web v2.6.0
 * @param {string} environment - 'QA' o 'PROD'
 * @returns {object} - API del hook
 */
export function usePinpadWeb(environment = 'QA') {
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const isInitialized = useRef(false)
  const initPromise = useRef(null)

  /**
   * Inicializa el iframe y MessageChannel
   * Puede llamarse múltiples veces, solo inicializa una vez
   * @returns {Promise<void>}
   */
  const initialize = useCallback(async () => {
    // Si ya está inicializando, retornar la promesa existente
    if (initPromise.current) {
      logInfo('Initialization already in progress, waiting...')
      return initPromise.current
    }

    // Si ya está inicializado, no hacer nada
    if (isInitialized.current) {
      logInfo('PinPad Web already initialized')
      return
    }

    // Crear nueva promesa de inicialización
    initPromise.current = (async () => {
      try {
        const initStart = Date.now()
        setIsLoading(true)
        setError(null)

        logInfo('Initializing PinPad Web v2.6.0', { environment })
        logFlowStep({
          phase: 'IFRAME',
          step: 'usePinpadWeb.initialize started',
          stepNumber: 6,
          status: 'START',
          data: {
            environment,
            isInitialized: isInitialized.current,
          },
        })

        // Paso 1: Crear iframe
        logInfo('Step 1: Creating iframe')
        const iframeStart = Date.now()
        await iframeService.createIframe(environment)
        logFlowStep({
          phase: 'IFRAME',
          step: 'Iframe created from usePinpadWeb.initialize',
          stepNumber: 7,
          status: 'SUCCESS',
          duration: Date.now() - iframeStart,
          data: {
            environment,
          },
        })
        
        // Paso 2: Inicializar MessageChannel
        logInfo('Step 2: Initializing MessageChannel')
        const channelStart = Date.now()
        await messageChannelService.initialize()
        logFlowStep({
          phase: 'CHANNEL',
          step: 'MessageChannel initialized from usePinpadWeb.initialize',
          stepNumber: 8,
          status: 'SUCCESS',
          duration: Date.now() - channelStart,
          data: {
            ready: messageChannelService.isReady(),
          },
        })

        isInitialized.current = true
        
        // Actualizar estado de forma síncrona usando callback
        // para garantizar que se actualice antes de resolver la promesa
        return new Promise(resolve => {
          setIsReady(true)
          // Delay mínimo para que React actualice el estado
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {

              logInfo('PinPad Web initialized successfully', {
                environment,
                iframeReady: iframeService.ready(),
                channelReady: messageChannelService.isReady()
              })
              logFlowStep({
                phase: 'INIT',
                step: 'usePinpadWeb.initialize completed',
                stepNumber: 9,
                status: 'SUCCESS',
                duration: Date.now() - initStart,
                data: {
                  environment,
                  iframeReady: iframeService.ready(),
                  channelReady: messageChannelService.isReady(),
                },
              })
              resolve()
            })
          })
        })
      } catch (err) {
        logFlowStep({
          phase: 'INIT',
          step: 'usePinpadWeb.initialize failed',
          stepNumber: 9,
          status: 'ERROR',
          error: {
            message: err?.message,
            code: err?.code,
            stack: err?.stack,
          },
        })
        logError('Failed to initialize PinPad Web', err)
        setError(err)
        isInitialized.current = false
        initPromise.current = null
        throw err
      } finally {
        setIsLoading(false)
      }
    })()

    return initPromise.current
  }, [environment])

  /**
   * Login con credenciales MIT
   * @param {object} credentials
   * @param {string} credentials.User
   * @param {string} credentials.Pwd
   * @param {string} credentials.Environment
   * @returns {Promise<object>}
   */
  const login = useCallback(async (credentials) => {
    // Verificar inicialización usando el ref directamente
    if (!isInitialized.current) {
      const error = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to login before initialization', { credentials: { User: credentials.User } })
      throw error
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await authService.login(credentials)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, []) // Removido isReady de dependencias

  /**
   * Obtiene llaves RSA
   * @param {object} params
   * @returns {Promise<object>}
   */
  const getKeysRSA = useCallback(async (params) => {
    // Verificar inicialización usando el ref directamente
    if (!isInitialized.current) {
      const error = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to getKeysRSA before initialization', { params: { User: params.User } })
      throw error
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await authService.getKeysRSA(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, []) // Removido isReady de dependencias

  const initValues = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to initValues before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await sessionService.initValues(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const readCard = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to readCard before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await cardService.readCard(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getMerchant = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to getMerchant before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await merchantService.getMerchant(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sndVentaDirectaEMV = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to sndVentaDirectaEMV before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await transactionService.sndVentaDirectaEMV(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sndReimpresion = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to sndReimpresion before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await transactionService.sndReimpresion(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const printVoucher = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to printVoucher before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await voucherService.printVoucher(params)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getRspVoucherComercio = useCallback(async () => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to getRspVoucherComercio before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await voucherService.getRspVoucherComercio()
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getRspVoucherCliente = useCallback(async () => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to getRspVoucherCliente before initialization', readyError)
      throw readyError
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await voucherService.getRspVoucherCliente()
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Procesa una compra completa (readCard + getMerchant + ventaDirecta)
   * @param {object} params
   * @param {string} params.reference - Referencia de pago
   * @param {number} params.amount - Monto en centavos (ej: 10000 = $100.00 MXN)
   * @param {string} params.currency - Código de moneda (default: 'MXN')
   * @param {string} params.currencyCode - Código ISO (default: '0484')
   * @returns {Promise<object>} - Respuesta de transacción exitosa
   */
  const processPurchase = useCallback(async (params) => {
    if (!isInitialized.current) {
      const readyError = new Error('PinPad Web not ready. Call initialize() first.')
      logError('Attempt to processPurchase before initialization', readyError)
      throw readyError
    }

    const {
      reference,
      amount,
      currency = 'MXN',
      currencyCode = '0484',
    } = params

    try {
      setIsLoading(true)
      setError(null)
      logInfo('Starting complete purchase flow', { reference, amount, currency })
      logFlowStep({
        phase: 'PURCHASE',
        step: 'processPurchase started',
        stepNumber: 14,
        status: 'START',
        data: { reference, amount, currency },
      })

      // ===== PASO 1: readCard =====
      const purchaseStart = Date.now()

      const credentials = sessionService.getCredentials()
      const terminalInfo = sessionService.getTerminalInfo()
      const userInfo = sessionService.getUserInfo()
      const env = sessionService.getEnvironment()

      if (!credentials || !terminalInfo || !userInfo) {
        throw new Error('Session info incomplete, ensure login/getKeys completed')
      }

      logFlowStep({
        phase: 'PURCHASE',
        step: 'Calling readCard',
        stepNumber: 15,
        status: 'START',
      })

      const readCardPayload = {
        Ambiente: env,
        Currency: currency,
        CurrencyCode: currencyCode,
        Amount: String(amount),
        MarcaTerminal: terminalInfo.marca,
        ModeloTerminal: terminalInfo.modelo,
        Reference: reference,
      }

      logInfo('processPurchase readCard payload', {
        payload: readCardPayload,
      })

      const readCardResult = await cardService.readCard(readCardPayload)

      logFlowStep({
        phase: 'PURCHASE',
        step: 'readCard completed',
        stepNumber: 16,
        status: 'SUCCESS',
        duration: Date.now() - purchaseStart,
        data: {
          maskPan: readCardResult.maskPan,
          name: readCardResult.name,
          cardBrand: readCardResult.cardBrand,
        },
      })

      // ===== PASO 2: getMerchant =====
      const bin = readCardResult.maskPan.substring(0, 6)
      logFlowStep({
        phase: 'PURCHASE',
        step: 'Calling getMerchant',
        stepNumber: 17,
        status: 'START',
        data: { bin, currency },
      })

      const getMerchantPayload = {
        Ambiente: env,
        BIN: bin,
        User: credentials.Usuario,
        Currency: currency,
        Tx_OperationType: '29',
      }

      logInfo('processPurchase getMerchant payload', {
        payload: getMerchantPayload,
      })

      const merchantStart = Date.now()
      const merchantResult = await merchantService.getMerchant(getMerchantPayload)

      logFlowStep({
        phase: 'PURCHASE',
        step: 'getMerchant completed',
        stepNumber: 18,
        status: 'SUCCESS',
        duration: Date.now() - merchantStart,
        data: {
          hasContado: !!merchantResult.contado,
          hasMSI: !!merchantResult.msi,
          hasMCI: !!merchantResult.plazosmci,
        },
      })

      // Extraer afiliación contado
      const contadoNode = merchantResult?.contado?.af
      const contado = Array.isArray(contadoNode) ? contadoNode[0] : contadoNode
      if (!contado || !contado.merchant) {
        throw new Error('No contado merchant available for this card')
      }

      // ===== PASO 3: sndVentaDirectaEMV =====
      logFlowStep({
        phase: 'PURCHASE',
        step: 'Calling sndVentaDirectaEMV',
        stepNumber: 19,
        status: 'START',
        data: {
          merchant: contado.merchant,
          reference,
        },
      })

      const ventaPayload = {
        Ambiente: env,
        Country: userInfo.bs_country,
        IdBranch: userInfo.bs_branch,
        IdCompany: userInfo.bs_company,
        pwd: credentials.Pass,
        User: credentials.Usuario,
        UserTRX: 'userPinpadWeb',
        EMV: terminalInfo.EMV,
        ModeloTerminal: terminalInfo.modelo,
        SerieTerminal: terminalInfo.serie,
        Contactless: terminalInfo.soportaCTLS,
        Printer: 1, //terminalInfo.impresora,
        VersionTerminal: terminalInfo.versionApp,
        TpOperation: 29,
        Reference: reference,
        Currency: currency,
        Merchant: contado.merchant,
        Reverse: userInfo.ExecuteReverse,
      }

      logInfo('processPurchase sndVentaDirectaEMV payload', {
        payload: ventaPayload,
      })

      const transactionStart = Date.now()
      const transactionResult = await transactionService.sndVentaDirectaEMV(ventaPayload)

      logFlowStep({
        phase: 'PURCHASE',
        step: 'sndVentaDirectaEMV completed',
        stepNumber: 20,
        status: 'SUCCESS',
        duration: Date.now() - transactionStart,
        data: {
          response: transactionResult.response,
          foliocpagos: transactionResult.foliocpagos,
          auth: transactionResult.auth,
          cd_response: transactionResult.cd_response,
        },
      })

      // Validar respuesta aprobada
      if (transactionResult.response !== 'approved') {
        throw new Error(
          `Transaction not approved: ${transactionResult.response} - ${transactionResult.nb_error || transactionResult.friendly_response}`
        )
      }

      let voucherPrinted = false
      let voucherPrintError = null
      let voucherPrintErrorCode = null

      // ===== PASO 4: imprimir voucher cliente =====
      try {
        const hasIntegratedPrinter = voucherService.hasIntegratedPrinter({
          impresora: terminalInfo.impresora,
          Printer: 1, //terminalInfo.impresora,
        })

        if (hasIntegratedPrinter) {
          let voucherCliente = transactionResult.voucher_cliente

          if (!voucherCliente) {
            logInfo('voucher_cliente not present in venta response, requesting getRspVoucherCliente')
            voucherCliente = await voucherService.getRspVoucherCliente()
          }

          if (voucherCliente) {
            const voucherPrintPayload = {
              Ambiente: env,
              VoucherCliente: voucherCliente,
              ModeloTerminal: terminalInfo.modelo,
            }

            logFlowStep({
              phase: 'VOUCHER',
              step: 'Printing customer voucher',
              stepNumber: 21,
              status: 'START',
              data: {
                hasVoucherCliente: !!voucherCliente,
                model: terminalInfo.modelo,
              },
            })

            logInfo('printVoucher payload prepared', {
              reference,
              foliocpagos: transactionResult.foliocpagos,
              model: terminalInfo.modelo,
              voucherLength: String(voucherCliente || '').length,
            })

            const printVoucherResponse = await voucherService.printVoucher(voucherPrintPayload)

            voucherPrinted = true

            logFlowStep({
              phase: 'VOUCHER',
              step: 'Customer voucher printed',
              stepNumber: 21,
              status: 'SUCCESS',
            })

            logInfo('Customer voucher printed successfully', {
              reference,
              model: terminalInfo.modelo,
              printVoucherResponse,
            })
          } else {
            logWarn('Customer voucher not available for printing', {
              reference,
              foliocpagos: transactionResult.foliocpagos,
            })
          }
        } else {
          logInfo('Skipping voucher print: terminal has no integrated printer', {
            reference,
            printer: 1, //terminalInfo.impresora,
          })
        }
      } catch (printError) {
        voucherPrintError = printError?.message || 'Voucher print failed'
        voucherPrintErrorCode = printError?.code || 'VOUCHER_PRINT_FAILED'

        logFlowStep({
          phase: 'VOUCHER',
          step: 'Customer voucher print failed',
          stepNumber: 21,
          status: 'ERROR',
          error: {
            message: printError?.message,
            code: printError?.code,
            stack: printError?.stack,
          },
        })

        logError('Customer voucher print failed, transaction remains approved', printError, {
          reference,
          foliocpagos: transactionResult.foliocpagos,
          model: terminalInfo?.modelo,
          printer: 1, //terminalInfo?.impresora,
          operation: 'PRINT_VOUCHER',
        })
      }

      logFlowStep({
        phase: 'COMPLETE',
        step: 'Purchase completed successfully',
        stepNumber: 22,
        status: 'SUCCESS',
        duration: Date.now() - purchaseStart,
        data: {
          reference: transactionResult.reference,
          foliocpagos: transactionResult.foliocpagos,
          auth: transactionResult.auth,
          amount: transactionResult.amount,
          cc_number: transactionResult.cc_number,
          cc_type: transactionResult.cc_type,
          voucherPrinted,
          voucherPrintError,
          voucherPrintErrorCode,
        },
      })

      return {
        success: true,
        charge: transactionResult,
        readCardData: readCardResult,
        merchantData: merchantResult,
        voucherPrinted,
        voucherPrintError,
        voucherPrintErrorCode,
      }
    } catch (error) {
      logError('Purchase flow failed', error, { reference, amount })
      logFlowStep({
        phase: 'PURCHASE',
        step: 'Purchase failed',
        stepNumber: 23,
        status: 'ERROR',
        error: {
          code: error?.code || 'PURCHASE_FAILED',
          message: error?.message,
          stack: error?.stack,
        },
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Reinicia completamente el iframe y channel
   * Útil para recovery después de errores críticos
   * @returns {Promise<void>}
   */
  const restart = useCallback(async () => {
    logInfo('Restarting PinPad Web')
    
    setIsLoading(true)
    setError(null)
    
    try {
      // Limpiar estado
      isInitialized.current = false
      initPromise.current = null
      setIsReady(false)

      // Reiniciar servicios
      messageChannelService.close()
      iframeService.destroy()

      // Reinicializar
      await initialize()
      
      logInfo('PinPad Web restarted successfully')
    } catch (err) {
      logError('Failed to restart PinPad Web', err)
      setError(err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [initialize])

  /**
   * Cleanup al desmontar el componente
   */
  useEffect(() => {
    return () => {
      if (isInitialized.current) {
        logInfo('Cleaning up PinPad Web on unmount')
        
        messageChannelService.close()
        iframeService.destroy()
        
        isInitialized.current = false
        initPromise.current = null
      }
    }
  }, [])

  return {
    // Estados
    isReady,
    isLoading,
    error,
    
    // Operaciones
    initialize,
    login,
    getKeysRSA,
    initValues,
    readCard,
    getMerchant,
    sndVentaDirectaEMV,
    sndReimpresion,
    printVoucher,
    getRspVoucherComercio,
    getRspVoucherCliente,
    processPurchase,
    restart,
    
    // Información útil
    environment,
    pendingMessages: messageChannelService.getPendingCount(),
    
    // Función getter para obtener el estado real de inicialización
    getIsInitialized: () => isInitialized.current
  }
}

export default usePinpadWeb
